import { type Thread, useStores } from '@littlebird/sdk';
import { formatRelativeDate } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useState, type MouseEvent } from 'react';

import { HiddenFromUserIcon } from '@/components/blocks/HiddenFromUserIcon';
import { XStack, YStack } from '@/components/ui';
import { RenameField } from '@/components/ui/RenameField/RenameField';
import { useRenameField } from '@/components/ui/RenameField/useRenameField';
import { useLastUserMessage } from '@/features/chat/chat.store';
import { ImChannelChatBadge } from '@/features/msg-channels/common/im-channel-chat-badge';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { beginChatDrag } from '@/utils/chat-drag';

import { ChatRowActions } from './ChatRowActions';
import { SelectableChatRow } from './SelectableChatRow';

export const ChatRow = observer(function ChatRow({
  thread,
  isSelected,
  selectionActive,
  onToggleSelect,
  onOpen,
}: {
  thread: Thread;
  isSelected: boolean;
  selectionActive: boolean;
  onToggleSelect: (id: string) => void;
  onOpen: (thread: Thread, event?: MouseEvent) => void;
}) {
  const { threads } = useStores();
  const { renameFieldRef, showRenameInputField } = useRenameField();
  const [actionsOpen, setActionsOpen] = useState(false);

  const title = thread.title || 'Untitled';
  const lastUpdated = thread.updatedAt ?? thread.createdAt;
  const lastUserMessage = useLastUserMessage(
    thread.id,
    // lastMessageId starts local refresh, status waits for persistence, and lastMessageAt catches remote sync updates
    `${thread.lastMessageId ?? ''}:${thread.status}:${thread.lastMessageAt.getTime()}`
  );

  return (
    <SelectableChatRow
      id={thread.id}
      label={title}
      isSelected={isSelected}
      selectionActive={selectionActive}
      onToggleSelect={onToggleSelect}
      onOpen={(event) => onOpen(thread, event)}
      draggable={!selectionActive}
      onDragStart={(event) => beginChatDrag(event, thread.id)}
      className={cn(
        'relative justify-between gap-4',
        actionsOpen && 'bg-surface-hover'
      )}
    >
      <YStack className="min-w-0 flex-1 gap-0.5">
        <XStack className="min-w-0 items-center gap-1.5">
          <p className="min-w-0 truncate text-foreground">
            <RenameField
              ref={renameFieldRef}
              value={title}
              onRename={(newTitle) => {
                threads.updateThread(thread, { title: newTitle });
                trackEvent('CHAT_TITLE_RENAMED', { chatId: thread.id });
              }}
            />
          </p>
          <HiddenFromUserIcon hidden={thread.hiddenFromUser} />
          <ImChannelChatBadge visible={thread.isMessagingChannel} />
        </XStack>
        <p className="min-h-[1.25rem] min-w-0 truncate text-sm text-foreground-muted">
          {lastUserMessage}
        </p>
      </YStack>
      {lastUpdated && (
        <span
          className={cn(
            'shrink-0 text-sm text-foreground-muted transition-opacity duration-150',
            actionsOpen && 'opacity-0',
            'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0'
          )}
        >
          {formatRelativeDate(lastUpdated)}
        </span>
      )}
      <ChatRowActions
        thread={thread}
        title={title}
        isSelected={isSelected}
        isOpen={actionsOpen}
        onOpenChange={setActionsOpen}
        onToggleSelect={() => onToggleSelect(thread.id)}
        onRename={() => setTimeout(showRenameInputField, 0)}
      />
    </SelectableChatRow>
  );
});
